// @ts-check
// Paleta de bloques del editor de diagramas (TSK-0022).
//
// Objetivo (docs/01-roadmap.md, Fase 1 / "§7"): mostrar los bloques visuales
// colocables del itinerario (Entrada, Salida, Asignación, Operación,
// Decisión, Bucle, Llamada a función, Retorno, Comentario, Subproceso) y
// permitir añadirlos al área de trabajo tanto arrastrando como con teclado.
//
// EXCEPCIÓN DE REAPERTURA (TSK-0055, DEC-026, docs/generated/decisions.md):
// "Inicio" y "Fin" YA NO son bloques de esta paleta. Por decisión de
// producto explícita del usuario (que sustituye la lectura literal de
// roadmap §4/§7), Inicio/Fin pasan a ser anclas fijas siempre presentes en
// `canvas-view.js`, no colocables/movibles/eliminables por el alumno: se
// retiran de aquí por completo (antes de TSK-0055 estaban en esta lista con
// `crearNodo: () => null`; ver el historial de TSK-0022 para ese diseño
// previo, ya superseded).
//
// SIMPLIFICACIÓN DE PALETA (TSK-0087, decisión de producto explícita del
// usuario): el bloque "Conector" se retira por completo de la paleta (no
// generaba ningún nodo IR propio, representaba solo la adyacencia implícita
// entre sentencias de una `Secuencia`, y resultaba confuso/sin propósito
// real para el alumno). Los bloques "Decisión" y "Condición" (ambos
// generaban el mismo nodo `Condicional`) se fusionan en un único bloque
// "Decisión" que SIEMPRE trae las dos ramas (si/si no); ver el comentario
// junto a ese bloque más abajo. Tras esta fusión, ningún bloque de la
// paleta deja `crearNodo()` devolver `null`, pero el tipo de `crearNodo`
// sigue admitiendo `null` (contrato estable de `DefinicionBloque`/
// `onSeleccionarBloque`, reutilizado también por "Inicio"/"Fin" en su
// diseño previo, ver más arriba) por si una tarea futura reintroduce un
// bloque sin nodo IR propio.
//
// Reutilización de arquitectura (NO se duplica, TSK-0021): cada bloque que sí
// tiene un nodo IR insertable lo construye llamando a una factoría real de
// `editor-model.js`. Este módulo nunca fabrica a mano la forma de un nodo del
// modelo lógico; solo añade metadatos de presentación (id de bloque, etiqueta
// visible) y, cuando una factoría exige argumentos, valores por defecto
// mínimos válidos (p.ej. un literal 0, una variable de nombre "variable").
//
// Límite de responsabilidad (docs/03-frontend.md §4.3 "components"): este
// módulo NO conoce el área de trabajo/canvas (todavía no existe ningún
// `canvas-view.js` en el árbol; no es una dependencia de esta tarea) ni la
// capa visual (`editor-visual-state.js`, fuera de las rutas permitidas de
// TSK-0022). Por eso expone un callback `onSeleccionarBloque(nodo,
// definicion)` que la vista de página/canvas que se construya después deberá
// inyectar; este módulo no importa fetch/services ni toca `editor-visual-state.js`.
//
// Accesibilidad (docs/03-frontend.md §13.1, "Drag tiene alternativa sin
// arrastre"; WCAG 2.2 SC 2.5.7): cada bloque es un <button> nativo dentro de
// una lista semántica (<ul><li>). Un <button> ya es operable con teclado
// (Enter/Espacio) y con ratón mediante el mismo evento "click" del
// navegador: por eso un único listener de "click" cubre AMBAS alternativas
// de forma equivalente e inmediata, sin añadir manejo de teclas a mano, sin
// `tabindex` (el orden de tabulación es el orden natural del documento) y
// sin recrear un control nativo con `div`+ARIA (03-frontend.md §6.2). El
// atributo `draggable` añade además la vía de arrastre con puntero/táctil:
// en `dragstart` se serializa un nodo recién creado (la misma factoría que
// usa el click) en `dataTransfer`, para que una futura área de trabajo lo
// lea en su propio `drop`. Arrastrar y activar por teclado producen el mismo
// nodo lógico a través de la misma factoría: son equivalentes por
// construcción, no dos implementaciones distintas que puedan divergir.

import {
  crearSecuencia,
  crearEntrada,
  crearSalida,
  crearAsignacion,
  crearOperacion,
  crearCondicional,
  crearBucleMientras,
  crearBucleFinPara,
  crearLlamada,
  crearRetorno,
  crearComentario,
  crearLiteral,
  crearReferenciaVariable,
  OPERADOR,
  TIPO_PRIMITIVO,
  TIPO_NODO,
} from './editor-model.js';

/**
 * @typedef {import('./editor-model.js').NodoModelo} NodoModelo
 */

/**
 * Definición de un bloque de paleta: metadatos de presentación (id
 * estable, etiqueta visible con texto) más una factoría que SIEMPRE delega
 * en `editor-model.js` para construir el nodo real.
 *
 * `crearNodo` puede devolver `null` (contrato estable heredado del diseño
 * previo de "Inicio"/"Fin"/"Conector", ver cabecera del archivo): ningún
 * bloque de la paleta actual devuelve `null` hoy, pero quien reciba
 * `onSeleccionarBloque(nodo, definicion)` (la vista de área de trabajo)
 * sigue debiendo tratar `nodo === null` como una intención válida, por si
 * una tarea futura reintroduce un bloque sin nodo IR propio.
 * `tipoVisual` (TSK-0079, rediseño visual con forma/color estándar por tipo):
 * el mismo valor de `TIPO_NODO` que `canvas-view.js` ya coloca en
 * `dataset.blockType` de cada bloque COLOCADO (ver el mapa tipo -> forma/
 * color documentado en la cabecera de ese archivo, TSK-0063/DEC-027, que
 * esta tarea reutiliza sin reinventarlo). Se declara aquí, por bloque de
 * PALETA, para dar color al pequeño icono decorativo de cada tarjeta
 * (`.palette-block-icon`, `crearElementoBloque` más abajo; mismo vocabulario
 * de tipo que el bloque colocado equivalente, nunca dos vocabularios que
 * puedan desincronizarse). Desde el mockup aprobado 2026-07-20 la propia
 * tarjeta de la paleta ya NO se recorta con la forma completa (eso quedó
 * reservado al bloque colocado real, ver comentario junto a `crearElemento
 * Bloque`): solo el icono la reproduce en miniatura. Para "Llamada a
 * función"/"Subproceso" (ambas -> `Llamada`) el `tipoVisual` sigue siendo
 * intencionalmente el mismo, igual que para los bloques colocados de esos
 * mismos ids (mismo nodo IR, misma forma).
 * `descripcion` (TSK-0153, DEC-058 punto 6, decisión de producto explícita
 * del usuario tras probar la app en vivo): frase breve en letra pequeña que
 * explica qué hace el bloque, mostrada DEBAJO del botón como contenido de
 * apoyo (no forma parte del nombre accesible del botón, ver
 * `crearElementoBloque` -- se asocia por `aria-describedby`, nunca dentro
 * del propio `<button>`, para no alargar/contaminar su nombre accesible).
 * @typedef {{ id: string, etiqueta: string, descripcion: string, crearNodo: () => NodoModelo | null, tipoVisual: string }} DefinicionBloque
 */

/** MIME con el nodo IR serializado (ausente si el bloque no crea nodo, ver `DefinicionBloque`). */
const MIME_BLOQUE = 'application/x-diagram-block+json';
/** MIME con el id de bloque, SIEMPRE presente (por si una tarea futura reintroduce un bloque sin nodo IR). */
const MIME_BLOQUE_ID = 'application/x-diagram-block-id';

/** Expresión mínima válida reutilizada como valor por defecto: un literal
 * entero 0, creado con la factoría real (nunca un objeto libre). */
function literalCero() {
  return crearLiteral({ valor: 0, tipo: TIPO_PRIMITIVO.ENTERO });
}

/** @param {number} valor */
function literalEntero(valor) {
  return crearLiteral({ valor, tipo: TIPO_PRIMITIVO.ENTERO });
}

/** Condición booleana mínima válida reutilizada por Decisión/Bucle. */
function condicionPorDefecto() {
  return crearOperacion({ operador: OPERADOR.IGUAL, operandos: [literalCero(), literalCero()] });
}

// --------------------------------------------------------------------------
// Los bloques colocables de docs/01-roadmap.md (Fase 1), SIN Inicio/Fin
// (retirados de la paleta por TSK-0055/DEC-026: ahora son anclas fijas de
// `canvas-view.js`, ver cabecera del archivo). El mapeo bloque→IR sigue la
// tabla canónica de backend/src/app/domain/ir.py (líneas 27-40, cabecera del
// módulo de dominio, derivada de roadmap §7/§23) -- fuente de verdad que
// corrige el mapeo original de esta tarea (ver historial de TSK-0022):
//
//   Entrada de datos     -> Entrada
//   Salida de datos      -> Salida
//   Asignación           -> Asignacion
//   Operación            -> Operacion
//   Decisión             -> Condicional (TSK-0087: bloque único, fusiona los
//                          antiguos "Decisión"/"Condición"; SIEMPRE trae
//                          ramaSi y ramaSino, ver comentario del bloque)
//   Bucle Mientras       -> BucleMientras (ver comentario del bloque)
//   Bucle Para           -> BucleFinPara (DEC-129: contador de un valor a otro)
//   Llamada a función    -> Llamada
//   Retorno              -> Retorno
//   Comentario           -> Comentario
//   Subproceso           -> Llamada a una Funcion (mismo nodo que "Llamada a
//                          función", diferenciado solo por nombre por defecto)
// --------------------------------------------------------------------------

/**
 * Id del párrafo que explica POR QUÉ está bloqueada la paleta. Se enlaza
 * desde el `aria-describedby` de cada botón mientras dura el bloqueo (ver
 * `establecerHabilitada`), que es la única vía por la que un lector de
 * pantalla llega a oírlo.
 */
const ID_AVISO_BLOQUEO = 'palette-aviso-bloqueo';

/** @type {readonly DefinicionBloque[]} */
export const PALETA_BLOQUES = Object.freeze([
  {
    id: 'entrada',
    etiqueta: 'Entrada de datos',
    descripcion: 'Pide un valor al usuario y lo guarda en una variable.',
    tipoVisual: TIPO_NODO.ENTRADA,
    crearNodo: () => crearEntrada({ variable: 'variable' }),
  },
  {
    id: 'salida',
    etiqueta: 'Salida de datos',
    descripcion: 'Muestra por pantalla un valor o un mensaje.',
    tipoVisual: TIPO_NODO.SALIDA,
    crearNodo: () => crearSalida({ expresion: crearReferenciaVariable({ nombre: 'variable' }) }),
  },
  {
    id: 'asignacion',
    etiqueta: 'Asignación',
    descripcion: 'Guarda el resultado de una expresión en una variable.',
    tipoVisual: TIPO_NODO.ASIGNACION,
    crearNodo: () => crearAsignacion({ variable: 'variable', expresion: literalCero() }),
  },
  // "Operación" se RETIRA de la paleta el 2026-08-05. No era un bloque
  // inofensivo de más: dejaba el diagrama INEJECUTABLE.
  //
  // El motivo está en la propia IR: `Operacion` pertenece a `Expresion`, no a
  // `Sentencia` (`domain/ir.py`). Es un trozo de fórmula -- "a + b" --, no un
  // paso del algoritmo, así que colocarla suelta en el lienzo producía un nodo
  // en posición de sentencia que el motor no sabe ejecutar. Comprobado contra
  // el motor real:
  //
  //   Salida(0)             -> exito=True   salidas=[0]
  //   Operacion suelta      -> exito=False  "Nodo de tipo desconocido en
  //                                          posición de sentencia: Operacion"
  //   Operacion + Salida(0) -> exito=False  <- rompe el programa ENTERO
  //
  // Un solo bloque de estos en cualquier punto tumbaba todo el diagrama, y el
  // mensaje no le dice nada a un alumno de instituto.
  //
  // Las operaciones NO desaparecen de la aplicación: se componen donde les
  // corresponde, dentro del editor de expresiones (`expression-editor.js`,
  // modo `'operacion'`), al rellenar una Asignación, una Decisión o un Bucle.
  // Ese `'operacion'` es OTRA cosa y no se toca.
  {
    id: 'decision',
    etiqueta: 'Decisión',
    descripcion: 'Bifurca el flujo según una condición: rama si / rama si no.',
    tipoVisual: TIPO_NODO.CONDICIONAL,
    // TSK-0087: bloque único que fusiona los antiguos "Decisión" y
    // "Condición" (ambos generaban el mismo nodo `Condicional`, ver ir.py
    // líneas 27-40; la única diferencia era si `ramaSino` se pasaba o no).
    // Decisión por decisión de producto SIEMPRE trae las dos ramas (si/si
    // no), ambas como `Secuencia` (aunque `ramaSino` quede vacía): el
    // alumno que no necesita la rama "si no" simplemente la deja vacía, el
    // motor de ejecución y el canvas ya soportan una rama vacía (TSK-0073
    // tiene el indicador visual para ese caso). Esta tarea NO añade ningún
    // control para alternar/quitar `ramaSino` tras colocar el bloque.
    crearNodo: () =>
      crearCondicional({
        condicion: condicionPorDefecto(),
        ramaSi: crearSecuencia(),
        ramaSino: crearSecuencia(),
      }),
  },
  {
    id: 'bucle',
    etiqueta: 'Bucle Mientras',
    descripcion: 'Repite un bloque mientras se cumpla una condición.',
    tipoVisual: TIPO_NODO.BUCLE_MIENTRAS,
    // roadmap Fase 1 lista un único bloque genérico "Bucle", pero ir.py
    // ofrece 3 variantes (BucleMientras/BucleFinPara/RepetirHasta). Se usa
    // BucleMientras (la más simple) como variante por defecto; elegir otra
    // variante se deja a una tarea futura de configuración de parámetros
    // (roadmap Fase 1: "Configurar sus parámetros"). El PARA tiene su
    // propio bloque desde DEC-129 ("Bucle Para", justo debajo), y este pasó a
    // llamarse "Bucle Mientras" para que los dos se distingan por el nombre.
    crearNodo: () => crearBucleMientras({ condicion: condicionPorDefecto(), cuerpo: crearSecuencia() }),
  },
  {
    id: 'bucle-para',
    etiqueta: 'Bucle Para',
    descripcion:
      'Repite un bloque un número fijo de veces: una variable cuenta desde un valor hasta otro.',
    tipoVisual: TIPO_NODO.BUCLE_FIN_PARA,
    // DEC-129: unos 15 ejercicios piden «usa un bucle PARA» y sus soluciones
    // lo usan. Arranca contando de 1 a 10 de 1 en 1: se puede ejecutar tal
    // cual y el paso nunca es 0, que el motor rechaza.
    crearNodo: () =>
      crearBucleFinPara({
        variable: 'i',
        inicio: literalEntero(1),
        fin: literalEntero(10),
        paso: literalEntero(1),
        cuerpo: crearSecuencia(),
      }),
  },
  {
    id: 'llamada-funcion',
    etiqueta: 'Llamada a función',
    descripcion: 'Ejecuta una función ya definida y usa su resultado.',
    tipoVisual: TIPO_NODO.LLAMADA,
    crearNodo: () => crearLlamada({ nombreFuncion: 'funcion', argumentos: [] }),
  },
  {
    id: 'retorno',
    etiqueta: 'Retorno',
    descripcion: 'Devuelve un valor y termina la función actual.',
    tipoVisual: TIPO_NODO.RETORNO,
    // Con valor por defecto, a diferencia de "Fin" (crearRetorno() sin
    // argumentos, expresión null).
    crearNodo: () => crearRetorno({ expresion: literalCero() }),
  },
  {
    id: 'comentario',
    etiqueta: 'Comentario',
    descripcion: 'Nota explicativa para el alumno; no se ejecuta.',
    tipoVisual: TIPO_NODO.COMENTARIO,
    crearNodo: () => crearComentario({ texto: 'Comentario' }),
  },
  // "Subproceso" se RETIRA de la paleta el 2026-08-05: era un duplicado exacto
  // de "Llamada a función". Lo admitía su propio comentario -- mismo nodo IR
  // (`Llamada`), misma ejecución, misma forma en el lienzo; la ÚNICA diferencia
  // era el nombre de función por defecto ('subproceso' frente a 'funcion'),
  // que el alumno reescribe en cuanto lo coloca.
  //
  // Dos bloques indistinguibles en la paleta no dan más capacidad: dan una
  // decisión falsa que hay que explicar en clase.
]);

/**
 * La definición de la paleta para un tipo de nodo del modelo (`tipoVisual`),
 * o `undefined` si la paleta no lo ofrece. Si hubiera dos del mismo tipo,
 * la primera. Es la fuente única del nombre legible de un tipo de bloque:
 * la usan el lienzo (avisos y nombres accesibles de lo que no colocó él) y el
 * controlador (título del contenido del bloque). DEC-180.
 * @param {string} tipo
 * @returns {DefinicionBloque | undefined}
 */
export function definicionDeTipo(tipo) {
  return PALETA_BLOQUES.find((definicion) => definicion.tipoVisual === tipo);
}

/**
 * @param {string} id
 * @returns {DefinicionBloque | undefined}
 */
function buscarDefinicionPorId(id) {
  return PALETA_BLOQUES.find((definicion) => definicion.id === id);
}

/**
 * @param {unknown} valor
 * @param {string} nombreCampo
 */
function requerirFuncion(valor, nombreCampo) {
  if (typeof valor !== 'function') {
    throw new TypeError(`${nombreCampo} debe ser una función`);
  }
  return valor;
}

/**
 * Busca, a partir del elemento donde ocurrió el evento, el <button> de
 * bloque más cercano dentro de `contenedor` (delegación con target
 * validado, docs/03-frontend.md §8.3: no se delega sin límites).
 *
 * Nota: la comprobación en tiempo de ejecución evita `instanceof
 * Element`/`instanceof HTMLButtonElement` como IDENTIFICADORES DE CÓDIGO
 * (usa en su lugar `typeof target.closest === 'function'` y `tagName ===
 * 'BUTTON'`), porque esos globals de tipo DOM no están declarados en
 * eslint.config.js (fuera de las rutas permitidas de esta tarea) y
 * `no-undef` los rechazaría como identificadores en tiempo de ejecución. Los
 * mismos nombres SÍ pueden usarse libremente en los tipos JSDoc de abajo:
 * son solo anotaciones para `tsc`, no código que ESLint evalúe.
 * @param {Element} contenedor
 * @param {EventTarget | null} target
 * @returns {HTMLButtonElement | null}
 */
function resolverBotonDeBloque(contenedor, target) {
  if (!target || typeof (/** @type {any} */ (target).closest) !== 'function') return null;
  const boton = /** @type {any} */ (target).closest('button[data-block-id]');
  if (!boton || boton.tagName !== 'BUTTON') return null;
  if (!contenedor.contains(boton)) return null;
  return /** @type {HTMLButtonElement} */ (boton);
}

/**
 * Construye el DOM de un bloque de la paleta (elemento de lista + botón).
 * DOM seguro (docs/03-frontend.md §8.2): solo `createElement`/`textContent`,
 * sin `innerHTML` ni handlers `onclick` inline.
 *
 * Descripción corta (TSK-0153, DEC-058 punto 6): se renderiza como un
 * `<span class="palette-block-description">` HERMANO del `<button>` (no
 * hijo), para que el texto NUNCA forme parte del nombre accesible del botón
 * (que sigue derivándose solo de `.palette-block-label`, ver
 * `boton.append(icono, etiqueta)` más abajo). Se asocia al botón mediante
 * `aria-describedby` con un id único derivado de `definicion.id`, así que un
 * lector de pantalla anuncia el NOMBRE del botón y, aparte, esta frase como
 * DESCRIPCIÓN adicional (semántica estándar de `aria-describedby`, WCAG
 * 2.2). El `<span>` no lleva `tabindex`: no es focalizable, no altera el
 * orden de tabulación (los `<button>` siguen siendo los únicos elementos
 * interactivos de la lista).
 * @param {DefinicionBloque} definicion
 * @returns {HTMLLIElement}
 */
function crearElementoBloque(definicion) {
  const item = document.createElement('li');
  item.className = 'palette-item';

  const idDescripcion = `palette-block-description-${definicion.id}`;

  const boton = document.createElement('button');
  boton.type = 'button';
  boton.className = 'palette-block';
  boton.draggable = true;
  boton.dataset.blockId = definicion.id;
  boton.setAttribute('aria-describedby', idDescripcion);
  // Se guarda para poder RECOMPONER `aria-describedby` al bloquear/desbloquear
  // sin perder la descripción funcional del bloque (ver `establecerHabilitada`).
  boton.dataset.descripcionId = idDescripcion;

  // Mockup aprobado (imagen de referencia, 2026-07-20; comparación directa
  // contra el Artifact "Dirección visual — Editor de diagramas + acceso"):
  // la tarjeta de la paleta es siempre un rectángulo plano de tamaño
  // uniforme -- la forma/color por tipo (TSK-0079) se reduce a un pequeño
  // icono decorativo a su izquierda (`.pal-shape`/`.pal-shape > i` en el
  // mockup), NO al recorte (`clip-path`) del botón completo como antes
  // (petición directa del usuario: "los bloques de la paleta... son muy
  // grandes" -- el padding extra que exige un rombo/hexágono/paralelogramo
  // recortado a tamaño completo era precisamente la causa). Los bloques YA
  // COLOCADOS en el canvas (`canvas-view.js`) SÍ conservan la forma completa
  // (siguen siendo el diagrama de flujo real); solo cambia la paleta, que es
  // chrome de la interfaz, no contenido del diagrama.
  const icono = document.createElement('span');
  icono.className = 'palette-block-icon';
  icono.setAttribute('aria-hidden', 'true');
  icono.dataset.blockType = definicion.tipoVisual;

  const etiqueta = document.createElement('span');
  etiqueta.className = 'palette-block-label';
  etiqueta.textContent = definicion.etiqueta;

  boton.append(icono, etiqueta);

  const descripcion = document.createElement('span');
  descripcion.className = 'palette-block-description';
  descripcion.id = idDescripcion;
  descripcion.textContent = definicion.descripcion;

  item.append(boton, descripcion);
  return item;
}

/**
 * Crea la vista de la paleta de bloques.
 *
 * @param {{ onSeleccionarBloque: (nodo: NodoModelo | null, definicion: DefinicionBloque) => void }} opciones
 * @returns {{ elemento: HTMLElement, establecerHabilitada: (habilitada: boolean, motivo?: string) => void, destruir: () => void }}
 */
export function crearVistaPaleta({ onSeleccionarBloque }) {
  requerirFuncion(onSeleccionarBloque, 'onSeleccionarBloque');

  const idTitulo = 'palette-heading';

  const seccion = document.createElement('section');
  seccion.className = 'palette ui-panel';
  seccion.setAttribute('aria-labelledby', idTitulo);

  const titulo = document.createElement('h2');
  titulo.id = idTitulo;
  titulo.className = 'palette-heading ui-panel-titulo ui-etiqueta';
  titulo.textContent = 'Paleta de bloques';

  const ayuda = document.createElement('p');
  ayuda.className = 'palette-hint';
  ayuda.textContent =
    'Cada bloque se puede añadir arrastrándolo al área de trabajo, o bien activándolo con Intro o Espacio.';

  // Por qué la paleta está bloqueada, cuando lo está. Región `status` (no
  // `alert`): aparece como consecuencia de elegir un ejercicio, no de un
  // fallo, así que no debe interrumpir a un lector de pantalla.
  const avisoBloqueo = document.createElement('p');
  avisoBloqueo.className = 'palette-aviso-bloqueo';
  avisoBloqueo.id = ID_AVISO_BLOQUEO;
  avisoBloqueo.setAttribute('role', 'status');
  avisoBloqueo.hidden = true;

  const lista = document.createElement('ul');
  lista.className = 'palette-list';
  lista.append(...PALETA_BLOQUES.map(crearElementoBloque));

  seccion.append(titulo, ayuda, avisoBloqueo, lista);

  /**
   * Activación por click O por teclado (Enter/Espacio): un <button> nativo
   * dispara "click" en ambos casos, así que esta única función ya es la
   * alternativa sin arrastre exigida por 03-frontend.md §13.1. `nodo` puede
   * ser `null` (contrato estable de `DefinicionBloque`, ver más arriba,
   * aunque ningún bloque de la paleta actual lo devuelva hoy); quien
   * inyectó `onSeleccionarBloque` decide qué hacer con esa intención.
   * @param {MouseEvent} evento
   */
  function manejarClick(evento) {
    const boton = resolverBotonDeBloque(lista, evento.target);
    if (!boton) return;
    const definicion = buscarDefinicionPorId(boton.dataset.blockId ?? '');
    if (!definicion) return;
    onSeleccionarBloque(definicion.crearNodo(), definicion);
  }

  /**
   * Vía de arrastre con ratón/táctil: serializa un nodo recién creado (con
   * la MISMA factoría que usa el click) en `dataTransfer`, para que una
   * futura área de trabajo lo consuma en su propio "drop". No invoca
   * `onSeleccionarBloque` aquí: iniciar un arrastre no equivale a soltarlo
   * (evitaría duplicar la inserción si además se completa el drop). El id
   * de bloque (`MIME_BLOQUE_ID`) se transmite SIEMPRE; `MIME_BLOQUE` (nodo
   * IR) solo si `crearNodo()` devuelve un nodo no nulo (ver
   * `DefinicionBloque`).
   * @param {DragEvent} evento
   */
  function manejarDragStart(evento) {
    const boton = resolverBotonDeBloque(lista, evento.target);
    if (!boton) return;
    const definicion = buscarDefinicionPorId(boton.dataset.blockId ?? '');
    if (!definicion || !evento.dataTransfer) return;
    const nodo = definicion.crearNodo();
    evento.dataTransfer.setData(MIME_BLOQUE_ID, definicion.id);
    evento.dataTransfer.setData('text/plain', definicion.etiqueta);
    if (nodo !== null) {
      evento.dataTransfer.setData(MIME_BLOQUE, JSON.stringify(nodo));
    }
    evento.dataTransfer.effectAllowed = 'copy';
  }

  lista.addEventListener('click', manejarClick);
  lista.addEventListener('dragstart', manejarDragStart);

  function destruir() {
    lista.removeEventListener('click', manejarClick);
    lista.removeEventListener('dragstart', manejarDragStart);
  }

  /**
   * Habilita o bloquea la paleta entera.
   *
   * Existe desde 2026-08-07: mientras se trabaja un ejercicio, no se puede
   * tocar el diagrama hasta pulsar "Iniciar intento" (petición del usuario).
   * Si no, se podía construir la solución entera con calma y arrancar el
   * cronómetro justo antes de corregir, y el tiempo medido no significaba
   * nada.
   *
   * Se deshabilitan los botones (`disabled`, que además los saca del orden de
   * tabulación y bloquea el teclado) Y se quita `draggable`, porque son las
   * DOS vías de añadir un bloque: dejar una abierta sería no bloquear nada.
   *
   * SIN ejercicio seleccionado la paleta está libre: es el modo borrador,
   * donde aterrizan las traducciones y donde se practica.
   *
   * @param {boolean} habilitada
   * @param {string} [motivo] Qué explicar en `title` cuando está bloqueada.
   */
  function establecerHabilitada(habilitada, motivo) {
    seccion.classList.toggle('palette-bloqueada', !habilitada);
    const botones = /** @type {NodeListOf<HTMLButtonElement>} */ (
      seccion.querySelectorAll('button.palette-block')
    );

    // Deshabilitar el elemento ENFOCADO tira el foco al `<body>` y quien
    // navega con teclado pierde su sitio. Se aparta antes, al encabezado de
    // la paleta, que es el ancla estable más cercana.
    if (!habilitada) {
      const enfocado = document.activeElement;
      if (enfocado instanceof globalThis.HTMLElement && seccion.contains(enfocado)) {
        titulo.tabIndex = -1;
        titulo.focus();
      }
    }

    for (const boton of botones) {
      boton.disabled = !habilitada;
      boton.draggable = habilitada;
      // El motivo se enlaza por `aria-describedby`, NO por `title`: cuando
      // hay `aria-describedby`, el navegador lo usa EN VEZ del `title`
      // (HTML-AAM), así que un lector de pantalla nunca llegaba a oír por qué
      // estaba bloqueado -- solo lo veía quien pasa el ratón por encima.
      // Hallazgo de accessibility-reviewer (2026-08-07).
      const descripcionPropia = boton.dataset.descripcionId ?? '';
      boton.setAttribute(
        'aria-describedby',
        habilitada || !motivo ? descripcionPropia : `${descripcionPropia} ${ID_AVISO_BLOQUEO}`,
      );
      if (habilitada || !motivo) boton.removeAttribute('title');
      else boton.title = motivo;
    }
    avisoBloqueo.hidden = habilitada;
    avisoBloqueo.textContent = habilitada ? '' : (motivo ?? '');
  }

  return { elemento: seccion, establecerHabilitada, destruir };
}
